<script setup>
import { usePage } from '@inertiajs/vue3'
import { computed, inject } from 'vue'

const __t = inject('__t')
const page = usePage()
const locale = computed(() => page.props.locale || 'en')
const isRtl = computed(() => locale.value === 'ar')

const initiatives = [
    {
        title: 'sr_academic_title',
        desc: 'sr_academic_desc',
        icon: 'mdi-school',
    },
    {
        title: 'sr_vocational_title',
        desc: 'sr_vocational_desc',
        icon: 'mdi-tools',
    },
    {
        title: 'sr_sustainability_title',
        desc: 'sr_sustainability_desc',
        icon: 'mdi-solar-power-variant',
    },
]
</script>

<template>
    <div class="relative overflow-hidden bg-primary py-24" :dir="isRtl ? 'rtl' : 'ltr'">
        <!-- Background Decorative Elements -->
        <div class="absolute inset-0 pointer-events-none overflow-hidden">
            <!-- Large Blob shape from the design -->
            <div class="absolute -top-24 -left-20 w-[120%] h-[120%] bg-blue-600/10 rounded-full blur-[100px]"></div>
            <div
                class="absolute top-[20%] -right-1/4 w-[100%] h-[80%] bg-blue-400/5 rounded-full blur-[120px] transform rotate-12">
            </div>

            <!-- Curved Wave Shape (approximation of the image) -->
            <svg class="absolute bottom-0 left-0 w-full h-auto opacity-10" viewBox="0 0 1440 320"
                preserveAspectRatio="none">
                <path fill="#ffffff" fill-opacity="1"
                    d="M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,181.3C960,181,1056,235,1152,234.7C1248,235,1344,181,1392,154.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z">
                </path>
            </svg>
        </div>

        <div class="container mx-auto px-6 relative z-10">
            <div class="max-w-6xl mx-auto">
                <!-- Section Title -->
                <div class="mb-20 text-center" data-aos="fade-up">
                    <h2 class="text-4xl md:text-6xl font-black text-white leading-tight mx-auto max-w-4xl">
                        {{ __t('sr_title') }}
                    </h2>
                    <div class="w-32 h-2 bg-secondary mt-6 mx-auto rounded-full"></div>
                </div>

                <!-- Initiatives Grid -->
                <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div v-for="(item, index) in initiatives" :key="index"
                        class="group relative bg-white/5 backdrop-blur-sm p-8 rounded-[2rem] border border-white/10 transition-all duration-500 hover:bg-white/10 hover:border-secondary/30 hover:-translate-y-2 flex flex-col items-center text-center"
                        data-aos="fade-up" :data-aos-delay="index * 150">

                        <!-- Icon Top -->
                        <div class="mb-8 relative">
                            <div
                                class="w-20 h-20 rounded-2xl bg-secondary/10 border-2 border-secondary/30 flex items-center justify-center transform group-hover:rotate-12 transition-all duration-500 shadow-xl shadow-secondary/5 group-hover:bg-secondary group-hover:border-white">
                                <v-icon size="40"
                                    class="text-secondary group-hover:text-primary transition-colors duration-500">
                                    {{ item.icon }}
                                </v-icon>
                            </div>
                            <!-- Pulse Effect -->
                            <div
                                class="absolute inset-0 bg-secondary rounded-2xl blur-xl opacity-0 group-hover:opacity-40 transition-opacity duration-500 scale-125 -z-10">
                            </div>
                        </div>

                        <!-- Content -->
                        <div class="space-y-4">
                            <h3
                                class="text-2xl font-bold text-white group-hover:text-secondary transition-colors duration-300">
                                {{ __t(item.title) }}
                            </h3>
                            <p class="text-gray-300 text-lg leading-relaxed">
                                {{ __t(item.desc) }}
                            </p>
                        </div>

                        <!-- Card Decorative Corner -->
                        <div class="absolute top-0 right-0 w-12 h-12 overflow-hidden rounded-tr-[2rem]">
                            <div
                                class="absolute top-0 right-0 w-1 bg-secondary/0 group-hover:bg-secondary h-full transition-all duration-500">
                            </div>
                            <div
                                class="absolute top-0 right-0 h-1 bg-secondary/0 group-hover:bg-secondary w-full transition-all duration-500">
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.container {
    max-width: 1400px;
}
</style>
